import { ArrowRight, Bot, BookOpenCheck, CheckCircle2, RotateCcw, ShieldCheck } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { EvidenceId, QuizProgress, SceneVisualState } from "../types";
import {
  ASSESSMENT_IDS,
  CHOICE_ASSESSMENTS,
  CORE_BLANKS,
  WORD_BANK,
  type AssessmentId,
  type ChoiceAssessment
} from "../content/assessments";
import {
  F01_ASSISTANT_CONTROL_IDS,
  F02_ASSISTANT_CONTROL_IDS,
  F02_ASSISTANT_PHRASE_IDS
} from "../../shared/assistantSceneState";
import { AssetImage } from "./AssetImage";
import { DragBoard, type DragItem, type DragTarget } from "./DragBoard";
import { InteractionFeedbackBanner, type InteractionFeedbackData, type InteractionFeedbackKind } from "./InteractionFeedback";
import "../chapters/chapterLabs.css";

type AuditStage = "classify" | "evidence" | "rewrite" | "done";

export interface QuizAssistantContext {
  sceneId: "F01" | "F02";
  currentItemId: string;
  controlValues: SceneVisualState["controlValues"];
}

interface QuizScreenProps {
  onAttempt: (correct: boolean, isFirstTry: boolean) => void;
  onEvidence: (paragraphId: string, title?: string) => void;
  onEvidenceViewed: () => void;
  onAssistantContextChange: (context: QuizAssistantContext) => void;
  onFinish: (firstScore: number, masteryScore: number) => void;
  /** Optional v2 persistence hook. It matches useProgress.saveQuizAnswer. */
  onQuestionResult?: (
    questionId: AssessmentId,
    answer: string | string[],
    correct: boolean,
    corrected: boolean
  ) => void;
  /** Called only after all three deterministic audit stages are correct. */
  onAuditComplete?: (evidenceIds: EvidenceId[]) => void;
  initialQuiz?: QuizProgress;
  initialAuditCompleted?: boolean;
}

const AUDIT_ITEMS = [
  {
    id: "audit-living",
    label: "物候观测使用活生生的生物，数据综合反映气候条件及其对生物的影响。",
    targetId: "keep"
  },
  {
    id: "audit-season",
    label: "南北物候差异的日数在每个季节都固定不变。",
    targetId: "revise"
  },
  {
    id: "audit-loss",
    label: "只要根据物候资料安排农事，就能避免所有农业损失。",
    targetId: "revise"
  },
  {
    id: "audit-factors",
    label: "纬度、经度、高下和古今差异会影响物候现象来临的迟早。",
    targetId: "keep"
  }
] satisfies DragItem[];

const AUDIT_TARGETS = [
  { id: "keep", label: "有证据，可保留", hint: "事实和结论没有超出课文内容" },
  { id: "revise", label: "表述不准，需修正", hint: "警惕“固定不变”“只要……就……”“所有”等说得过满的表述" }
] satisfies DragTarget[];

const SEASON_EVIDENCE = ["P07", "P08", "P09", "P10"] as const satisfies readonly EvidenceId[];
const LOSS_EVIDENCE = ["P04", "P05", "P11", "P12"] as const satisfies readonly EvidenceId[];
const AUDIT_EVIDENCE = ["P04", "P05", "P07", "P08", "P09", "P10", "P11"] as const satisfies readonly EvidenceId[];
const CORE_EVIDENCE_LABELS: Partial<Record<EvidenceId, string>> = {
  P03: "物候概念",
  P08: "经度因素",
  P09: "高下因素",
  P07: "纬度因素"
};

const SEASON_PHRASES = ["会因季节不同", "南北物候差异的日数", "而变化"] as const;
const SEASON_SOLUTION = ["南北物候差异的日数", "会因季节不同", "而变化"] as const;
const LOSS_PHRASES = ["有些损失", "可能因此避免或减轻", "物候资料可为农时和播种日期等提供参考"] as const;
const LOSS_SOLUTION = ["物候资料可为农时和播种日期等提供参考", "有些损失", "可能因此避免或减轻"] as const;

const F01_CORE_CONTROLS = {
  phenology: F01_ASSISTANT_CONTROL_IDS.corePhenology,
  longitude: F01_ASSISTANT_CONTROL_IDS.coreLongitude,
  elevation: F01_ASSISTANT_CONTROL_IDS.coreElevation,
  latitude: F01_ASSISTANT_CONTROL_IDS.coreLatitude
} as const;
const F01_CHOICE_CONTROLS: Readonly<Record<Exclude<AssessmentId, "core">, string>> = {
  structure: F01_ASSISTANT_CONTROL_IDS.structure,
  methods: F01_ASSISTANT_CONTROL_IDS.methods,
  "case-1962": F01_ASSISTANT_CONTROL_IDS.case1962,
  attitude: F01_ASSISTANT_CONTROL_IDS.attitude
};
const F02_SEASON_EVIDENCE_CONTROLS: Readonly<Record<(typeof SEASON_EVIDENCE)[number], string>> = {
  P07: F02_ASSISTANT_CONTROL_IDS.seasonP07,
  P08: F02_ASSISTANT_CONTROL_IDS.seasonP08,
  P09: F02_ASSISTANT_CONTROL_IDS.seasonP09,
  P10: F02_ASSISTANT_CONTROL_IDS.seasonP10
};
const F02_LOSS_EVIDENCE_CONTROLS: Readonly<Record<(typeof LOSS_EVIDENCE)[number], string>> = {
  P04: F02_ASSISTANT_CONTROL_IDS.lossP04,
  P05: F02_ASSISTANT_CONTROL_IDS.lossP05,
  P11: F02_ASSISTANT_CONTROL_IDS.lossP11,
  P12: F02_ASSISTANT_CONTROL_IDS.lossP12
};
const F02_SEASON_PHRASE_IDS = new Map<string, string>([
  [SEASON_PHRASES[0], F02_ASSISTANT_PHRASE_IDS.seasonCondition],
  [SEASON_PHRASES[1], F02_ASSISTANT_PHRASE_IDS.seasonDifference],
  [SEASON_PHRASES[2], F02_ASSISTANT_PHRASE_IDS.seasonChange]
]);
const F02_LOSS_PHRASE_IDS = new Map<string, string>([
  [LOSS_PHRASES[0], F02_ASSISTANT_PHRASE_IDS.lossScope],
  [LOSS_PHRASES[1], F02_ASSISTANT_PHRASE_IDS.lossOutcome],
  [LOSS_PHRASES[2], F02_ASSISTANT_PHRASE_IDS.lossReference]
]);

/** 只编码权威选项序号；学生可见答案正文不会进入助手请求快照。 */
export function buildF01AssistantControlValues(
  currentId: AssessmentId | undefined,
  blankAnswers: Readonly<Record<string, string>>,
  choiceAnswers: Readonly<Record<string, number>>
): SceneVisualState["controlValues"] {
  if (!currentId) return {};
  if (currentId !== "core") {
    const selectedOption = choiceAnswers[currentId];
    return Number.isInteger(selectedOption)
      ? { [F01_CHOICE_CONTROLS[currentId]]: selectedOption }
      : {};
  }

  return Object.fromEntries(CORE_BLANKS.flatMap((blank) => {
    const word = blankAnswers[blank.id];
    const optionIndex = WORD_BANK.indexOf(word as (typeof WORD_BANK)[number]);
    return optionIndex >= 0 ? [[F01_CORE_CONTROLS[blank.id], optionIndex]] : [];
  }));
}

function phraseSlotValues(
  values: readonly string[],
  phraseIds: ReadonlyMap<string, string>,
  slotIds: readonly string[]
): SceneVisualState["controlValues"] {
  return Object.fromEntries(values.flatMap((value, index) => {
    const phraseId = phraseIds.get(value);
    const slotId = slotIds[index];
    return phraseId && slotId ? [[slotId, phraseId]] : [];
  }));
}

/** 审校草稿只编码证据勾选布尔值和权威短语 ID，不上传短语正文。 */
export function buildF02AssistantControlValues(
  stage: AuditStage,
  state: Readonly<{
    seasonEvidence: readonly EvidenceId[];
    lossEvidence: readonly EvidenceId[];
    evidenceAttempted: boolean;
    seasonRewrite: readonly string[];
    lossRewrite: readonly string[];
    rewriteAttempted: boolean;
  }>
): SceneVisualState["controlValues"] {
  if (stage === "evidence") {
    return {
      ...Object.fromEntries(SEASON_EVIDENCE.map((id) => [F02_SEASON_EVIDENCE_CONTROLS[id], state.seasonEvidence.includes(id)])),
      ...Object.fromEntries(LOSS_EVIDENCE.map((id) => [F02_LOSS_EVIDENCE_CONTROLS[id], state.lossEvidence.includes(id)])),
      [F02_ASSISTANT_CONTROL_IDS.evidenceAttempted]: state.evidenceAttempted
    };
  }
  if (stage === "rewrite") {
    return {
      ...phraseSlotValues(state.seasonRewrite, F02_SEASON_PHRASE_IDS, [
        F02_ASSISTANT_CONTROL_IDS.seasonSlot1,
        F02_ASSISTANT_CONTROL_IDS.seasonSlot2,
        F02_ASSISTANT_CONTROL_IDS.seasonSlot3
      ]),
      ...phraseSlotValues(state.lossRewrite, F02_LOSS_PHRASE_IDS, [
        F02_ASSISTANT_CONTROL_IDS.lossSlot1,
        F02_ASSISTANT_CONTROL_IDS.lossSlot2,
        F02_ASSISTANT_CONTROL_IDS.lossSlot3
      ]),
      [F02_ASSISTANT_CONTROL_IDS.rewriteAttempted]: state.rewriteAttempted
    };
  }
  return {};
}

function sameMembers(actual: readonly string[], expected: readonly string[]) {
  return actual.length === expected.length && expected.every((item) => actual.includes(item));
}

function sameOrder(actual: readonly string[], expected: readonly string[]) {
  return actual.length === expected.length && actual.every((item, index) => item === expected[index]);
}

function readInitialBlankAnswers(initialQuiz?: QuizProgress): Record<string, string> {
  const answer = initialQuiz?.correctedAnswers.core ?? initialQuiz?.firstAnswers.core;
  if (!Array.isArray(answer)) return {};
  return Object.fromEntries(CORE_BLANKS.map((blank, index) => [blank.id, answer[index] ?? ""]));
}

function readInitialChoiceAnswers(initialQuiz?: QuizProgress): Record<string, number> {
  return Object.fromEntries(CHOICE_ASSESSMENTS.flatMap((assessment) => {
    const answer = initialQuiz?.correctedAnswers[assessment.id] ?? initialQuiz?.firstAnswers[assessment.id];
    const parsed = typeof answer === "string" ? Number.parseInt(answer, 10) : Number.NaN;
    return Number.isInteger(parsed) ? [[assessment.id, parsed]] : [];
  }));
}

function QuizBackdrop() {
  return (
    <div className="quiz-artwork" aria-hidden="true">
      <AssetImage assetId="6A-01" alt="" sizes="100vw" eager />
    </div>
  );
}

/** Five deterministic assessments followed by a three-stage, evidence-bound AI audit. */
export function QuizScreen({
  onAttempt,
  onEvidence,
  onEvidenceViewed,
  onAssistantContextChange,
  onFinish,
  onQuestionResult,
  onAuditComplete,
  initialQuiz,
  initialAuditCompleted = false
}: QuizScreenProps) {
  const initialMastered = initialQuiz?.masteredQuestionIds.filter((id): id is AssessmentId =>
    ASSESSMENT_IDS.includes(id as AssessmentId)
  ) ?? [];
  const initialIndex = ASSESSMENT_IDS.findIndex((id) => !initialMastered.includes(id));
  const [groupIndex, setGroupIndex] = useState(initialIndex === -1 ? ASSESSMENT_IDS.length : initialIndex);
  const [firstAnswers, setFirstAnswers] = useState<Record<string, string | string[]>>(() => ({
    ...initialQuiz?.firstAnswers
  }));
  const [firstCorrectIds, setFirstCorrectIds] = useState<AssessmentId[]>(() =>
    initialQuiz?.firstCorrectQuestionIds.filter((id): id is AssessmentId => ASSESSMENT_IDS.includes(id as AssessmentId)) ?? []
  );
  const [masteredIds, setMasteredIds] = useState<AssessmentId[]>(initialMastered);
  const [blankAnswers, setBlankAnswers] = useState<Record<string, string>>(() => readInitialBlankAnswers(initialQuiz));
  const [activeBlank, setActiveBlank] = useState(CORE_BLANKS[0].id as string);
  const [choiceAnswers, setChoiceAnswers] = useState<Record<string, number>>(() => readInitialChoiceAnswers(initialQuiz));
  const [attemptCounts, setAttemptCounts] = useState<Record<string, number>>({});
  const [readyToContinue, setReadyToContinue] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const auditSheetRef = useRef<HTMLElement>(null);
  const previousStepKeyRef = useRef<string | null>(null);
  const feedbackId = useRef(0);
  const [feedback, setFeedback] = useState<InteractionFeedbackData | null>({
    id: 0,
    kind: "info",
    message: "先独立作答；需要时再打开课文证据核验。",
    title: "作答提示"
  });

  function showFeedback(kind: InteractionFeedbackKind, message: string, title?: string) {
    feedbackId.current += 1;
    setFeedback({ id: feedbackId.current, kind, message, title });
  }

  const [auditStage, setAuditStage] = useState<AuditStage>(initialAuditCompleted ? "done" : "classify");
  const [seasonEvidence, setSeasonEvidence] = useState<EvidenceId[]>([]);
  const [lossEvidence, setLossEvidence] = useState<EvidenceId[]>([]);
  const [evidenceAttempted, setEvidenceAttempted] = useState(false);
  const [seasonRewrite, setSeasonRewrite] = useState<string[]>(initialAuditCompleted ? [...SEASON_SOLUTION] : []);
  const [lossRewrite, setLossRewrite] = useState<string[]>(initialAuditCompleted ? [...LOSS_SOLUTION] : []);
  const [rewriteAttempted, setRewriteAttempted] = useState(false);

  const assessmentsDone = groupIndex >= ASSESSMENT_IDS.length;
  const currentId = ASSESSMENT_IDS[groupIndex];
  const choiceAssessment = groupIndex > 0 && groupIndex < ASSESSMENT_IDS.length
    ? CHOICE_ASSESSMENTS[groupIndex - 1]
    : undefined;
  const currentEvidenceIds = useMemo<readonly EvidenceId[]>(() => groupIndex === 0
    ? CORE_BLANKS.map((blank) => blank.evidence)
    : choiceAssessment?.evidenceIds ?? AUDIT_EVIDENCE,
  [choiceAssessment, groupIndex]);
  const stepKey = assessmentsDone ? `audit:${auditStage}` : `assessment:${groupIndex}`;

  useEffect(() => {
    const previousStepKey = previousStepKeyRef.current;
    previousStepKeyRef.current = stepKey;
    if (previousStepKey === null || previousStepKey === stepKey) return;

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    auditSheetRef.current?.scrollTo?.({ top: 0, left: 0, behavior: "auto" });
    const focusFrame = window.requestAnimationFrame(() => {
      headingRef.current?.focus({ preventScroll: true });
    });

    return () => window.cancelAnimationFrame(focusFrame);
  }, [stepKey]);

  const assistantContext = useMemo<QuizAssistantContext>(() => {
    if (!assessmentsDone) {
      return {
        sceneId: "F01",
        currentItemId: `item.f01.${currentId ?? "core"}`,
        controlValues: buildF01AssistantControlValues(currentId, blankAnswers, choiceAnswers)
      };
    }
    return {
      sceneId: "F02",
      currentItemId: `item.f02.${auditStage}`,
      controlValues: buildF02AssistantControlValues(auditStage, {
        seasonEvidence,
        lossEvidence,
        evidenceAttempted,
        seasonRewrite,
        lossRewrite,
        rewriteAttempted
      })
    };
  }, [
    assessmentsDone,
    auditStage,
    blankAnswers,
    choiceAnswers,
    currentId,
    evidenceAttempted,
    lossEvidence,
    lossRewrite,
    rewriteAttempted,
    seasonEvidence,
    seasonRewrite
  ]);

  useEffect(() => {
    onAssistantContextChange(assistantContext);
  }, [assistantContext, onAssistantContextChange]);

  function assignWord(word: string) {
    if (readyToContinue) return;
    setBlankAnswers((current) => {
      const next = { ...current };
      const existingBlank = CORE_BLANKS.find((blank) => next[blank.id] === word)?.id;
      if (existingBlank) delete next[existingBlank];
      next[activeBlank] = word;
      return next;
    });
    const currentPosition = CORE_BLANKS.findIndex((blank) => blank.id === activeBlank);
    const nextBlank = CORE_BLANKS.slice(currentPosition + 1).find((blank) => !blankAnswers[blank.id]);
    if (nextBlank) setActiveBlank(nextBlank.id);
  }

  function currentAnswer(): string | string[] {
    if (currentId === "core") return CORE_BLANKS.map((blank) => blankAnswers[blank.id] ?? "");
    return String(choiceAnswers[currentId] ?? "");
  }

  function currentAnswerIsComplete() {
    if (currentId === "core") return CORE_BLANKS.every((blank) => Boolean(blankAnswers[blank.id]));
    return choiceAnswers[currentId] !== undefined;
  }

  function currentAnswerIsCorrect() {
    if (currentId === "core") return CORE_BLANKS.every((blank) => blankAnswers[blank.id] === blank.answer);
    return choiceAssessment !== undefined && choiceAnswers[currentId] === choiceAssessment.answer;
  }

  function submitAssessment() {
    if (!currentId || !currentAnswerIsComplete() || readyToContinue) return;
    const answer = currentAnswer();
    const correct = currentAnswerIsCorrect();
    const firstTry = !(currentId in firstAnswers);
    const nextAttemptCount = (attemptCounts[currentId] ?? 0) + 1;
    setAttemptCounts((current) => ({ ...current, [currentId]: nextAttemptCount }));
    onAttempt(correct, firstTry);
    onQuestionResult?.(currentId, answer, correct, !firstTry && correct);

    if (firstTry) setFirstAnswers((current) => ({ ...current, [currentId]: answer }));
    if (!correct) {
      const hint = currentId === "core"
        ? "核对课文内容：区分概念名称与影响因素。"
        : choiceAssessment?.correctionHint;
      showFeedback(
        "error",
        nextAttemptCount === 1
          ? `${hint ?? "请先核对对应课文。"} 当前答案已保留，可以直接订正。`
          : `再看证据中的关键词：${hint ?? "结论不能超过课文范围。"}`,
        "这组还未答对"
      );
      return;
    }

    if (firstTry) setFirstCorrectIds((current) => current.includes(currentId) ? current : [...current, currentId]);
    setMasteredIds((current) => current.includes(currentId) ? current : [...current, currentId]);
    showFeedback("success", firstTry ? "第一次作答正确，这组已完成。" : "订正正确，这组已完成。第一次作答结果仍会保留。");
    setReadyToContinue(true);
  }

  function continueAssessment() {
    if (!readyToContinue) return;
    setGroupIndex((current) => current + 1);
    setReadyToContinue(false);
    showFeedback("info", "先独立作答；需要时再打开课文证据核验。", "已进入下一组");
  }

  function verifyEvidence(evidenceId: EvidenceId, title: string) {
    onEvidenceViewed();
    onEvidence(evidenceId, title);
  }

  function toggleEvidence(
    evidenceId: EvidenceId,
    selected: EvidenceId[],
    setSelected: (value: EvidenceId[]) => void
  ) {
    setSelected(selected.includes(evidenceId)
      ? selected.filter((id) => id !== evidenceId)
      : [...selected, evidenceId]);
  }

  function submitAuditEvidence() {
    const correct = sameMembers(seasonEvidence, ["P07"]) && sameMembers(lossEvidence, ["P05", "P11"]);
    onAttempt(correct, !evidenceAttempted);
    setEvidenceAttempted(true);
    if (!correct) {
      showFeedback("error", "季节差异要找南北日数的直接表述；修正农业结论时，既要找课文P05 的案例，也要找课文P11 对物候资料用途的说明。已选证据仍会保留。", "证据选择还不准确");
      return;
    }
    showFeedback("success", "证据选择正确。现在把两条说得过满的表述重组成准确句。");
    setAuditStage("rewrite");
  }

  function togglePhrase(phrase: string, selected: string[], setSelected: (value: string[]) => void) {
    setSelected(selected.includes(phrase) ? selected.filter((item) => item !== phrase) : [...selected, phrase]);
  }

  function submitAuditRewrite() {
    const correct = sameOrder(seasonRewrite, SEASON_SOLUTION) && sameOrder(lossRewrite, LOSS_SOLUTION);
    onAttempt(correct, !rewriteAttempted);
    setRewriteAttempted(true);
    if (!correct) {
      showFeedback("error", "先说明资料的用途，再说明“可能”产生的结果。已选短语仍然保留，可以直接重排。", "语序还不准确");
      return;
    }
    showFeedback("success", "审校完成：两条表述都已根据课文证据修正。");
    setAuditStage("done");
    onAuditComplete?.([...AUDIT_EVIDENCE]);
  }

  function finishJourney() {
    if (masteredIds.length !== ASSESSMENT_IDS.length || auditStage !== "done") return;
    onFinish(firstCorrectIds.length, masteredIds.length);
  }

  if (assessmentsDone) {
    const auditStep = auditStage === "classify" ? 1 : auditStage === "evidence" ? 2 : 3;
    return (
      <main className="quiz-screen quiz-screen--audit">
        <QuizBackdrop />
        <section ref={auditSheetRef} className="audit-sheet" aria-labelledby="audit-title">
          <div
            className="quiz-progress"
            role="progressbar"
            aria-label="AI 草稿审校进度"
            aria-valuemin={1}
            aria-valuemax={3}
            aria-valuenow={auditStep}
            aria-valuetext={`第 ${auditStep} 步，共 3 步`}
          >
            <span style={{ width: `${auditStep / 3 * 100}%` }} />
          </div>
          <span className="section-kicker"><Bot size={16} /> AI 草稿审校 · {auditStep}/3</span>
          <h1
            ref={headingRef}
            id="audit-title"
            tabIndex={-1}
            aria-label={`用课文证据审校 AI 草稿，第 ${auditStep} 步，共 3 步`}
          >
            用课文证据审校 AI 草稿
          </h1>
          {feedback && <InteractionFeedbackBanner feedback={feedback} onDismiss={() => setFeedback(null)} className="quiz-feedback" />}

          {auditStage === "classify" && (
            <>
              <div className="ai-draft">
                <span>第 1 步 · 分类四条草稿</span>
                <blockquote>判断每条草稿是“有证据，可保留”，还是“表述不准，需修正”。</blockquote>
              </div>
              <DragBoard
                compact
                items={AUDIT_ITEMS}
                targets={AUDIT_TARGETS}
                onAttempt={onAttempt}
                onComplete={() => {
                  setAuditStage("evidence");
                  showFeedback("success", "四条草稿已分类。接下来为两条不准确的表述选择直接证据。");
                }}
              />
            </>
          )}

          {auditStage === "evidence" && (
            <>
              <div className="ai-draft">
                <span>第 2 步 · 选择课文证据</span>
                <blockquote>分别选择能直接修正“南北物候差异的日数在每个季节都固定不变”和“只要根据物候资料安排农事，就能避免所有农业损失”的课文段落，可多选。</blockquote>
              </div>
              <div className="audit-evidence-grid">
                <div>
                  <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
                    <legend>南北物候差异的日数在每个季节都固定不变</legend>
                    {SEASON_EVIDENCE.map((id) => (
                      <label className="quiz-radio-option" key={id}>
                        <input
                          type="checkbox"
                          checked={seasonEvidence.includes(id)}
                          onChange={() => toggleEvidence(id, seasonEvidence, setSeasonEvidence)}
                        />
                        <span><strong>课文{id}</strong> {id === "P07" ? "· 纬度与季节差异" : id === "P08" ? "· 经度与海陆差异" : id === "P09" ? "· 高下与逆温" : "· 古今差异"}</span>
                      </label>
                    ))}
                  </fieldset>
                </div>
                <div>
                  <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
                    <legend>只要根据物候资料安排农事，就能避免所有农业损失</legend>
                    {LOSS_EVIDENCE.map((id) => (
                      <label className="quiz-radio-option" key={id}>
                        <input
                          type="checkbox"
                          checked={lossEvidence.includes(id)}
                          onChange={() => toggleEvidence(id, lossEvidence, setLossEvidence)}
                        />
                        <span><strong>课文{id}</strong> {id === "P04" ? "· 活的仪器" : id === "P05" ? "· 1962 年北京案例" : id === "P11" ? "· 物候资料的农业用途" : "· 加强物候观测"}</span>
                      </label>
                    ))}
                  </fieldset>
                </div>
              </div>
              <div className="choice-ribbon" style={{ marginTop: 16 }}>
                {["P05", "P07", "P11"].map((id) => (
                  <button key={id} type="button" onClick={() => verifyEvidence(id as EvidenceId, `AI 草稿审校 · ${id}`)}>
                    <BookOpenCheck size={17} /> 核对课文 {id}
                  </button>
                ))}
              </div>
              <div className="chapter-actions">
                <button
                  className="command-button"
                  type="button"
                  aria-describedby={seasonEvidence.length === 0 || lossEvidence.length === 0 ? "quiz-audit-evidence-requirements" : undefined}
                  data-progress-ready={seasonEvidence.length > 0 && lossEvidence.length > 0 ? "true" : "false"}
                  title={seasonEvidence.length === 0 || lossEvidence.length === 0 ? "点击查看还需完成的内容" : undefined}
                  onClick={() => seasonEvidence.length > 0 && lossEvidence.length > 0
                    ? submitAuditEvidence()
                    : showFeedback("error", "两组草稿都至少选择一条候选证据后才能核对。", "证据尚未选全")}
                >
                  确认证据 <CheckCircle2 size={18} />
                </button>
                {(seasonEvidence.length === 0 || lossEvidence.length === 0) && (
                  <span className="sr-only" id="quiz-audit-evidence-requirements">两组草稿都至少选择一条候选证据。</span>
                )}
              </div>
            </>
          )}

          {(auditStage === "rewrite" || auditStage === "done") && (
            <>
              <div className="ai-draft">
                <span>第 3 步 · 重组准确表述</span>
                <blockquote>按顺序点选短语。再次点击已选短语可撤回；两句话都准确后才能生成档案。</blockquote>
              </div>
              <div className="audit-rewrite">
                <strong>修正一 · 南北季节差异</strong>
                <p>{seasonRewrite.length > 0 ? seasonRewrite.join("，") : "尚未组合"}</p>
                <div className="choice-ribbon">
                  {SEASON_PHRASES.map((phrase) => (
                    <button className={seasonRewrite.includes(phrase) ? "is-selected" : ""} key={phrase} type="button" disabled={auditStage === "done"} onClick={() => togglePhrase(phrase, seasonRewrite, setSeasonRewrite)}>
                      {phrase}{seasonRewrite.includes(phrase) ? ` · ${seasonRewrite.indexOf(phrase) + 1}` : ""}
                    </button>
                  ))}
                  {auditStage !== "done" && <button type="button" onClick={() => setSeasonRewrite([])}><RotateCcw size={15} /> 重排</button>}
                </div>
              </div>
              <div className="audit-rewrite">
                <strong>修正二 · 物候资料的作用范围</strong>
                <p>{lossRewrite.length > 0 ? lossRewrite.join("，") : "尚未组合"}</p>
                <div className="choice-ribbon">
                  {LOSS_PHRASES.map((phrase) => (
                    <button className={lossRewrite.includes(phrase) ? "is-selected" : ""} key={phrase} type="button" disabled={auditStage === "done"} onClick={() => togglePhrase(phrase, lossRewrite, setLossRewrite)}>
                      {phrase}{lossRewrite.includes(phrase) ? ` · ${lossRewrite.indexOf(phrase) + 1}` : ""}
                    </button>
                  ))}
                  {auditStage !== "done" && <button type="button" onClick={() => setLossRewrite([])}><RotateCcw size={15} /> 重排</button>}
                </div>
              </div>
              <div className="chapter-actions">
                {auditStage === "rewrite" ? (
                  <button
                    className="command-button"
                    type="button"
                    aria-describedby={seasonRewrite.length !== 3 || lossRewrite.length !== 3 ? "quiz-audit-rewrite-requirements" : undefined}
                    data-progress-ready={seasonRewrite.length === 3 && lossRewrite.length === 3 ? "true" : "false"}
                    title={seasonRewrite.length !== 3 || lossRewrite.length !== 3 ? "点击查看还需完成的内容" : undefined}
                    onClick={() => seasonRewrite.length === 3 && lossRewrite.length === 3
                      ? submitAuditRewrite()
                      : showFeedback("error", "两句话都需要各选择三个短语，并按正确语序排列。", "短语尚未组合完整")}
                  >
                    完成审校 <ShieldCheck size={18} />
                  </button>
                ) : (
                  <button
                    className="command-button"
                    type="button"
                    aria-describedby={masteredIds.length !== ASSESSMENT_IDS.length ? "quiz-report-requirements" : undefined}
                    data-progress-ready={masteredIds.length === ASSESSMENT_IDS.length ? "true" : "false"}
                    title={masteredIds.length !== ASSESSMENT_IDS.length ? "点击查看还需完成的内容" : undefined}
                    onClick={() => masteredIds.length === ASSESSMENT_IDS.length
                      ? finishJourney()
                      : showFeedback("error", "仍有正式测验项目尚未订正，完成全部五组后才能生成档案。", "档案尚未解锁")}
                  >
                    生成能力档案 <ArrowRight size={18} />
                  </button>
                )}
                {auditStage === "rewrite" && (seasonRewrite.length !== 3 || lossRewrite.length !== 3) && (
                  <span className="sr-only" id="quiz-audit-rewrite-requirements">两句话都要各选满三个短语。</span>
                )}
                {auditStage === "done" && masteredIds.length !== ASSESSMENT_IDS.length && (
                  <span className="sr-only" id="quiz-report-requirements">完成全部五组正式测验后才能生成档案。</span>
                )}
              </div>
            </>
          )}
        </section>
      </main>
    );
  }

  const activeTitle = groupIndex === 0 ? "核心知识与信息筛选" : choiceAssessment?.title;
  const completedAssessmentCount = Math.min(
    ASSESSMENT_IDS.length,
    groupIndex + (readyToContinue ? 1 : 0)
  );
  return (
    <main className="quiz-screen">
      <QuizBackdrop />
      <div
        className="quiz-progress"
        role="progressbar"
        aria-label="正式测验掌握进度"
        aria-valuemin={0}
        aria-valuemax={ASSESSMENT_IDS.length}
        aria-valuenow={completedAssessmentCount}
        aria-valuetext={`已掌握 ${completedAssessmentCount} 组，共 ${ASSESSMENT_IDS.length} 组`}
      >
        <span style={{ width: `${completedAssessmentCount / ASSESSMENT_IDS.length * 100}%` }} />
      </div>
      <span className="section-kicker">正式测验 {groupIndex + 1}/5 · {activeTitle}</span>
      <h1 ref={headingRef} tabIndex={-1}>
        {groupIndex === 0 ? "根据课文完成四项判断" : choiceAssessment?.prompt}
      </h1>
      {feedback && <InteractionFeedbackBanner feedback={feedback} onDismiss={() => setFeedback(null)} className="quiz-feedback" />}

      {groupIndex === 0 ? (
        <div className="quiz-core-layout">
          <div className="quiz-options quiz-core-questions" role="group" aria-label="四个待判断问题">
            {CORE_BLANKS.map((blank, index) => (
              <button
                className={`core-blank ${activeBlank === blank.id ? "is-selected" : ""}`}
                type="button"
                key={blank.id}
                aria-pressed={activeBlank === blank.id}
                disabled={readyToContinue}
                onClick={() => setActiveBlank(blank.id)}
              >
                <span>{index + 1}</span>
                <span>
                  <span className="quiz-core-question">{blank.prompt}</span>
                  <span className="quiz-core-answer"><small>当前答案</small><strong>{blankAnswers[blank.id] || "待选择"}</strong></span>
                </span>
              </button>
            ))}
          </div>
          <aside className="word-bank quiz-core-word-bank" aria-label="答案词库">
            <strong>词库</strong>
            <div className="choice-ribbon">
              {WORD_BANK.map((word) => {
                const used = Object.values(blankAnswers).includes(word);
                return (
                  <button className={used ? "is-selected" : ""} type="button" key={word} disabled={readyToContinue} onClick={() => assignWord(word)}>
                    {word}{used ? " · 已放置" : ""}
                  </button>
                );
              })}
            </div>
          </aside>
        </div>
      ) : choiceAssessment ? (
        <fieldset className="quiz-options quiz-radio-list">
          <legend className="sr-only">请选择一个答案</legend>
          {choiceAssessment.options.map((option, optionIndex) => (
            <label className={`quiz-radio-option ${choiceAnswers[currentId] === optionIndex ? "is-selected" : ""}`} key={option}>
              <input
                type="radio"
                name={currentId}
                value={optionIndex}
                checked={choiceAnswers[currentId] === optionIndex}
                disabled={readyToContinue}
                onChange={() => setChoiceAnswers((current) => ({ ...current, [currentId]: optionIndex }))}
              />
              <span>{String.fromCharCode(65 + optionIndex)}. {option}</span>
            </label>
          ))}
        </fieldset>
      ) : null}

      <div className="choice-ribbon" style={{ marginTop: 18 }} aria-label="课文证据入口">
        {currentEvidenceIds.map((id) => (
          <button key={id} type="button" onClick={() => verifyEvidence(id, `正式测验 ${groupIndex + 1} · ${id}`)}>
            <BookOpenCheck size={17} /> {groupIndex === 0 ? CORE_EVIDENCE_LABELS[id] ?? `课文${id}` : `课文${id}`}
          </button>
        ))}
      </div>
      <div className="chapter-actions">
        {readyToContinue ? (
          <button className="command-button" type="button" onClick={continueAssessment}>
            {groupIndex === 4 ? "进入 AI 审校" : "下一组"} <ArrowRight size={18} />
          </button>
        ) : (
          <>
            <button
              className="command-button"
              type="button"
              aria-describedby={!currentAnswerIsComplete() ? "quiz-answer-requirements" : undefined}
              data-progress-ready={currentAnswerIsComplete() ? "true" : "false"}
              title={!currentAnswerIsComplete() ? "点击查看还需完成的内容" : undefined}
              onClick={() => currentAnswerIsComplete()
                ? submitAssessment()
                : showFeedback("error", currentId === "core" ? "四个空格都填写后才能确认判断。" : "请先选择一个答案。", "作答尚未完成")}
            >
              确认判断 <CheckCircle2 size={18} />
            </button>
            {!currentAnswerIsComplete() && (
              <span className="sr-only" id="quiz-answer-requirements">
                {currentId === "core" ? "四个空格都填写后才能确认判断。" : "请先选择一个答案。"}
              </span>
            )}
          </>
        )}
      </div>
    </main>
  );
}
